import { Box, Typography, FormControlLabel, Checkbox, useTheme, useMediaQuery } from '@mui/material';
import { motion } from 'framer-motion';

interface Props {
  agreed: boolean;
  onChange: (checked: boolean) => void;
}

export default function PrivacyConsentBox({ agreed, onChange }: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 20, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.6 }}
      sx={{
        p: { xs: 2, md: 3 },
        mb: { xs: 2, md: 4 },
        background: 'rgba(255,255,255,0.65)',
        borderRadius: 4,
        boxShadow: '0 4px 32px 0 rgba(43,64,176,0.10)',
        backdropFilter: 'blur(16px)',
        border: '1.5px solid rgba(139,92,246,0.08)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Floating blur gradient effect */}
      <Box sx={{
        position: 'absolute', top: -25, left: -30, width: 85, height: 85,
        background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
        opacity: 0.13, filter: 'blur(23px)', borderRadius: '50%', zIndex: 0
      }} />
      <Box sx={{
        position: 'absolute', bottom: -30, right: -30, width: 70, height: 70,
        background: 'linear-gradient(135deg, #a5b4fc 0%, #f0abfc 100%)',
        opacity: 0.12, filter: 'blur(20px)', borderRadius: '50%', zIndex: 0
      }} />

      <Typography
        variant={isMobile ? "body1" : "subtitle1"}
        fontWeight={700}
        gutterBottom
        sx={{
          mb: 1,
          background: 'linear-gradient(90deg,#0ea5e9 0%,#8b5cf6 80%)',
          WebkitBackgroundClip: 'text',
          color: 'transparent',
          letterSpacing: 0.5,
          zIndex: 1
        }}
      >
        [필수] 개인정보 수집 및 이용 동의
      </Typography>
      <Box
        sx={{
          maxHeight: 155,
          overflowY: 'auto',
          border: '1.5px solid #dde5fa',
          borderRadius: 2,
          p: { xs: 1.1, md: 2 },
          mb: 2,
          bgcolor: 'rgba(248,250,252,0.92)',
          fontSize: isMobile ? 13 : 15,
          boxShadow: '0 1.5px 9px 0 rgba(139,92,246,0.03)',
          zIndex: 1,
        }}
      >
        <Typography variant="body2" paragraph sx={{ fontWeight: 500 }}>
          아래와 같이 귀하의 개인정보를 수집 및 이용합니다.
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>수집 항목:</strong> 이름, 연락처(휴대전화), 주소, 입금계좌(예금주, 계좌번호, 은행명 등)<br />
          <strong>수집 목적:</strong> 본인 확인, 고객 상담 및 서비스 제공, 계약 이행<br />
          <strong>보유 및 이용 기간:</strong> 수집일로부터 1년 또는 회원 탈퇴 시까지<br />
          <strong>고충 처리 :</strong> 민원인의 신원 확인, 민원사항 확인, 사실조사를 위한 연락․통지, 처리 결과 통보 등 <br />
          <strong>재화 또는 서비스 제공 :</strong> 물품 배송, 서비스 제공, 계약서․청구서 발송, 콘텐츠 제공, 맞춤 서비스 제공, <br /> 본인인증, 연령인증, 요금 결제 및 정산, 채권추심 등의 목적<br />
          <strong>동의 거부 권리:</strong> 필수 항목에 동의하지 않으면 서비스 이용이 불가할 수 있습니다.
        </Typography>
        <Typography variant="body2" sx={{ color: '#6366f1', fontWeight: 600 }}>
          위 내용을 충분히 이해하였으며, 개인정보 수집 및 이용에 동의합니다.
        </Typography>
      </Box>
      <FormControlLabel
        control={
          <Checkbox
            checked={agreed}
            onChange={(e) => onChange(e.target.checked)}
            sx={{
              color: '#3b82f6',
              '&.Mui-checked': {
                color: 'linear-gradient(90deg, #0ea5e9 0%, #8b5cf6 100%)',
              },
              transition: 'all 0.13s',
              mr: 1
            }}
          />
        }
        label={
          <span style={{
            fontWeight: 600,
            background: agreed
              ? 'linear-gradient(90deg,#0ea5e9 0%,#8b5cf6 80%)'
              : 'inherit',
            WebkitBackgroundClip: agreed ? 'text' : undefined,
            color: agreed ? 'transparent' : '#374151',
            transition: 'all 0.22s',
             fontSize: isMobile ? 15 : 16,
          }}>
            개인정보 수집 및 이용에 동의합니다.
          </span>
        }
        sx={{ zIndex: 1, mt: 1, userSelect: 'none' }}
      />
    </Box>
  );
}
